












import {Component, Prop, PropSync, Vue, Watch} from 'vue-property-decorator';
import PxDropdown from "@/views/components/input/formFields/PxDropdown.vue";
import PxNumberInput from "@/views/components/input/formFields/PxNumberInput.vue";
import {
  logNormalDataSet,
  normalDataSet, triangularDataSet, truncatedLogNormalDataSet, truncatedNormalDataSet,
  uniformDataSet
} from '@/modules/lcca/lib/calculations/probabilityDistributionChart.ts';

var d3 = Object.assign(
  {},
  require('d3-shape'),
  require('d3-selection'),
  require('d3-axis'),
  require('d3-array')
);

import * as d3Scale from 'd3-scale';
import * as d3Axis from 'd3-axis';
import {IDistribution} from "@/lib/monteCarlo/interfaces";

interface IPoint{
  x: number, y:number
}

Object.assign(d3, d3Scale);
@Component({
  components: {
    PxDropdown,
    PxNumberInput
  },
})
export default class ProbabilisticChart extends Vue {
  @Prop() value!: IDistribution;
  @Prop() deterministicValue!: number;
  @Prop({default: 'chart_0'}) id!: string;
  @Prop({default: 5}) defaultXMax!: number;
  @Prop({default: 0}) defaultXMin!: number;
  @Prop({default: 0}) forceUpdateCounter!: number;
  @Prop() distDetails!: any;
  @Prop() xLabel!: string;

  margin = {
    left: 80,
    top: 20,
    right: 20,
    bottom: 50
  };

  padding = 20;

  @Watch('value', {immediate: true, deep: true})
  onValueChanged(value: IDistribution, oldValue: IDistribution) {
    // console.log("value updated in chart", value);
    this.setPdfData();
    this.createChartGroup()
  }

  @Watch('forceUpdateCounter')
  onUpdateCounterChange(value: number, oldValue: number){
    // console.log("updateCounter triggered", this.id, this.forceUpdateCounter);
    this.setPdfData();
    this.createChartGroup()
  }

  get axesElementId() {
    return this.id + '_axes'
  }

  get chartCanvasElementId() {
    return this.id + '_chartCanvas'
  }

  get plotElementId() {
    return this.id + '_plot'
  }

  get chartContainerId(){
    return this.id + '_container'
  }

  mounted() {
    this.setPdfData();
    window.addEventListener("resize", this.setChartSize);
    this.setChartSize();
    this.createChartGroup();
  }

  updated() {
    // console.log('updated');
    this.$emit('input', this.value);
    this.createChartGroup()
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.setChartSize)
  }

  /*
  Chart logic
   */

  chartWidth: number = 0;
  chartHeight: number = 200;

  setChartSize() {
    this.setChartWidth();
    this.setChartHeight();
  }

  setChartWidth() {
    const width = document.getElementById(this.chartContainerId) ?
      //@ts-ignore
      document.getElementById(this.chartContainerId).offsetWidth : 0;

    if (width === 0) {
      // console.log("Unable to get chart width.")
    }
    this.chartWidth = width;
  }

  setChartHeight() {
    const height = document.getElementById(this.chartContainerId) ?
      //@ts-ignore
      document.getElementById(this.chartContainerId).offsetHeight : 0;
    if (height === 0) {
      // console.log("Unable to get chart height.")
    }
    this.chartHeight = height;
  }


  pdfData: {x:number, y:number}[] = [];

  setPdfData() {
    // console.debug("Setting PDF data");
    const min = this.value.min || 0;
    const mean = this.value.mean || 0;
    const mode = this.value.mode || 0;
    const max = this.value.max || 0;
    const std = this.value.std || 0;

    let data: {x:number, y:number}[] = [];

    switch (this.value.prob) {
      case 'deterministic':
        data = [
          {x: this.deterministicValue, y:0},
          {x: this.deterministicValue, y: 1}
        ];
        break;
      //   drawGraph(deterministicDataSet(mean));
      // $('#graphInfo').text("The probabilistic model is Deterministic with mean: " + mean);
      // break;
      case 'uniform':
        // console.warn("uniform");
        data = uniformDataSet(min, max);
        break;
      // $('#graphInfo').text("The probabilistic model is Uniform with min: " + min + " and max: " + max);
      case 'normal':
        // console.warn("normal");
        data = normalDataSet(mean, std);
        break;
      // $('#graphInfo').text("The probabilistic model is Normal with mean: " + mean + " and standard deviation: " + std);
      case 'lognormal':
        data = logNormalDataSet(mean, std);
      // $('#graphInfo').text("The probabilistic model is Log Normal with mean: " + mean + " and standard deviation: " + std);
        break;
      case 'triangular':
        data = triangularDataSet(min, max, mode);
      // $('#graphInfo').text("The probabilistic model is Triangular with min: " + min + " and max: " + max + " and mode: " + mode);
        break;
      case 'truncatednormal':
        data = truncatedNormalDataSet(mean, std, min, max);
        break;
      // $('#graphInfo').text("The probabilistic model is Truncated Normal with min: " + min + ", max: " + max + ", mean: " + mean + " and standard deviation: " + std);
      case 'truncatedlognormal':
        data = truncatedLogNormalDataSet(mean, std, min, max);
        break;
      // $('#graphInfo').text("The probabilistic model is Truncated Log Normal with min: " + min + ", max: " + max + ", mean: " + mean + " and standard deviation: " + std);
    }

    this.pdfData = data;
  }

  get xScale() {
    const data = this.pdfData;

    const maxXValue = d3.max(data, (d: IPoint) => d.x);

    let maxToUse: number = this.defaultXMax;
    if (this.distDetails.max){ // There is an explicit max
      maxToUse = maxXValue > this.defaultXMax ? maxXValue : this.defaultXMax;
    } else if (this.value.prob === 'normal'){
      const fourStdDev = (this.value.std || 0.1) * 4;
      const normMax = (this.value.mean || 1) + fourStdDev;
      maxToUse = normMax > this.defaultXMax ? normMax : this.defaultXMax
    }

    const minXValue = d3.min(data, (d: IPoint) => d.x);
    const minToUse = minXValue < this.defaultXMin ? minXValue : this.defaultXMin;

    let x = d3Scale.scaleLinear()
      .domain([
        // d3.min(data, d => d.x),
        minToUse,
        maxToUse
      ])
      .range([this.margin.left, this.chartWidth - this.margin.right]);

    return x
  }

  get yScale() {
    const data = this.pdfData;
    let y = d3Scale.scaleLinear()
      .domain([
        0,
        d3.max(data, (d: IPoint) => d.y)
      ])
      .range([this.chartHeight - this.margin.bottom, this.margin.top]);

    return y

  }


  setAxes() {
    // console.log('Creating axes');
    this.setChartWidth();

    let xAxis = d3Axis.axisBottom(this.xScale);
    let yAxis = d3Axis.axisLeft(this.yScale);

    const axesRoot = d3.select("#" + this.axesElementId);

    axesRoot.selectAll('*')
      .remove();

    axesRoot.append('g')
      .attr("transform", "translate (0," + (this.chartHeight - this.margin.bottom) + ")") // move down x
      .call(xAxis);

    axesRoot.append('g')
      .attr("transform", "translate (" + this.margin.left + ", 0)") // move left y
      .call(yAxis);


    // Create axis labels
    axesRoot.append("text")
      .attr("transform", "translate(" + (this.chartWidth/ 2) + " ," +
        (this.chartHeight - (this.margin.bottom / 4)) + ")")
      .style("text-anchor", "middle")
      .text(this.xLabel);

    axesRoot.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", this.margin.left / 4)
      .attr("x", 0 - (this.chartHeight / 2))
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text("Probability");

    const zeroLine = d3.line()
      .x((d: IPoint) => this.xScale(d.x))
      .y((d: IPoint) => this.yScale(d.y));

    axesRoot.append('path')
      .data([
        {x: 0, y: 0},
        {x: 0, y: 1}
        ])
      .attr('class', 'line')
      .attr('d', zeroLine);

    return {x: xAxis, y: yAxis}
  }


  createChartGroup() {
    const width = this.chartWidth;
    const height = this.chartHeight;
    const canvas = d3.select("#" + this.chartCanvasElementId).attr("class", "chart");
    this.setAxes();

    // console.log("switching dist plot", this.id);

    // canvas.selectAll("*").remove();

    canvas.append("g")
      .attr("width", width - this.margin.left - this.margin.right)
      .attr("height", height - this.margin.top - this.margin.bottom)
      .attr("transform", "translate(100,100)")
      .attr("padding", 20);

    // Plot the data
    const line = d3.line()
      .x((d: IPoint) => this.xScale(d.x))
      .y((d: IPoint) => this.yScale(d.y))
      .curve(d3.curveMonotoneX);

    try {
      d3.select("#" + this.plotElementId)//.append("path")
        .data([this.pdfData])
        .attr('class','line')
        .attr('d', line);
    }  catch (e) {
      console.log("D3 Error", this.pdfData)
    }
  }

}
